import {
  forwardRef,
  PropsWithChildren,
  ReactNode,
  RefObject,
  useId,
} from 'react';
import { Collapse, collapseClasses, SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CollapseIcon from './CollapseIcon';

import { useRouter } from 'next/router';

import ArrowLeftIcon from '@watershed/icons/components/ArrowLeft';
import PopOutIcon from '@watershed/icons/components/PopOut';
import SidebarRightIcon from '@watershed/icons/components/SidebarRight';
import { SIDEBAR_EXPANDED_WIDTH } from '@watershed/shared-universal/utils/constants';
import mixinSx from '@watershed/style/mixinSx';

import { useViewTransition } from '../hooks/useViewTransition';
import BoxLink from './BoxLink';
import Button from './Button';

export type NavigationItemType = {
  id: string;
  label: ReactNode;
  href: string;
  external?: boolean;
};

export type NavigationGroupType = {
  label: string;
  icon: ReactNode;
  items: Array<NavigationItemType>;
};

// Extract styles to a separate object for better reusability
const MAIN_STYLES = {
  navigationContainer: (theme: Theme, isExpanded: boolean, width: number) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width,
    py: 0.5,
    paddingInlineStart: 2,
    paddingInlineEnd: 1.5,
    backgroundColor: theme.palette.grey10,
    transition: 'transform 300ms ease-out',
    transform: 'translateX(0)',
    zIndex: 1000,
    [theme.breakpoints.down('md')]: {
      width: 'fit-content',
      transform: isExpanded ? 'translateX(0)' : 'translateX(-100%)',
      borderRight: `0.5px solid ${theme.palette.grey30}`,
      pr: 1.5,
      borderTopRightRadius: '6px',
      borderBottomRightRadius: '6px',
      boxShadow: `0px 0px 1px 0px ${theme.palette.grey100}05, 0px 0px 2px 0px ${theme.palette.grey100}05`,
    },
  }),
  collapsedStyles: (theme: Theme) => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    paddingX: 2,
    paddingY: 1,
    display: 'none',
    zIndex: 900,
    backgroundColor: 'transparent',
    width: 'fit-content',

    [`@media screen and (max-width: ${theme.breakpoints.values.md}px)`]: {
      display: 'block',
    },
  }),
  itemLayout: {
    display: 'grid',
    gridTemplateColumns: '12px 1fr',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0.5,
  },
  sharedItemStyles: {
    border: 0,
    gridColumn: '2/2',
    textDecoration: 'none',
    paddingX: 1,
    paddingY: 0.5,
    borderRadius: 1,
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: (theme: Theme) => `${theme.palette.grey20}80`,
    },
  },
  activeItemStyles: {
    backgroundColor: (theme: Theme) => theme.palette.grey20,
    borderRadius: 1,
  },
  backLinkStyles: {
    mt: 0.5,
    mb: 1,
    display: 'flex',
    alignItems: 'center',
    direction: 'row',
    width: 'fit-content',
    cursor: 'pointer',
    gap: 1,
    ml: -1,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '> svg': {
      fill: (theme: Theme) => theme.palette.grey50,
    },
  },
};

const IN_PAGE_STYLES: Record<string, SxProps<Theme>> = {
  inPageContainer: {
    width: SIDEBAR_EXPANDED_WIDTH,
    height: '100%',
    maxHeight: '50vb',
    overflowY: 'auto',
    position: 'sticky',
    scrollSnapType: 'y proximity',
    top: 0,
  },
  inPageSection: {
    paddingInline: 1.5,
    paddingBlockStart: 1.5,
    scrollSnapAlign: 'start',
  },
  inPageItem: {
    scrollSnapAlign: 'start',
    textDecoration: 'none',
    textWrap: 'pretty',
    outlineColor: (theme) => theme.palette.primary.main,
    cursor: 'pointer',
    borderRadius: 1,
    paddingInline: 1,
    paddingBlock: 0.5,
    display: 'flex',
    alignItems: 'center',
    transition: (theme) =>
      theme.transitions.create(['background-color'], {
        duration: theme.transitions.duration.short,
      }),
    '&:hover': {
      backgroundColor: (theme) => `${theme.palette.grey10}80`,
    },
    '&[aria-current="true"]': {
      backgroundColor: (theme) => theme.palette.grey10,
    },
  },
};

/**
 * A composable navigation system that provides a consistent way to build navigation interfaces.
 * Includes support for mobile-responsive behavior, breadcrumbs, and nested navigation.
 *
 * @example
 * ```tsx
 * <Navigation.Root isExpanded={isExpanded}>
 *   <Navigation.CollapsedHeader onClick={toggleExpanded}>
 *     <Navigation.Breadcrumb>
 *       <Navigation.Breadcrumb.List>
 *         <Navigation.Breadcrumb.Item>
 *           <Navigation.Breadcrumb.Text>Home</Navigation.Breadcrumb.Text>
 *         </Navigation.Breadcrumb.Item>
 *         <Navigation.Breadcrumb.Separator />
 *         <Navigation.Breadcrumb.Item>
 *           <Navigation.Breadcrumb.Icon>{icon}</Navigation.Breadcrumb.Icon>
 *           <Navigation.Breadcrumb.Text>Settings</Navigation.Breadcrumb.Text>
 *         </Navigation.Breadcrumb.Item>
 *       </Navigation.Breadcrumb.List>
 *     </Navigation.Breadcrumb>
 *   </Navigation.CollapsedHeader>
 *
 *   <Navigation.BackLink onClick={handleBack}>
 *     <Navigation.Label>Back</Navigation.Label>
 *   </Navigation.BackLink>
 *
 *   <Navigation.Group icon={<Icon />} label="Group">
 *     <Navigation.Item href="/path">
 *       <Navigation.Label>Item</Navigation.Label>
 *     </Navigation.Item>
 *   </Navigation.Group>
 * </Navigation.Root>
 * ```
 */
function Navigation({
  children,
  isExpanded,
  width = SIDEBAR_EXPANDED_WIDTH,
  ref,
}: {
  children: ReactNode;
  isExpanded: boolean;
  width?: number;
  ref?: RefObject<HTMLDivElement>;
}) {
  return (
    <Stack
      component="nav"
      ref={ref}
      gap={1}
      sx={(theme) => MAIN_STYLES.navigationContainer(theme, isExpanded, width)}
    >
      {children}
    </Stack>
  );
}

/**
 * Container for navigation header content with icon and label.
 *
 * @example
 * ```tsx
 * <Navigation.Header icon={<Icon />}>Section Title</Navigation.Header>
 * ```
 */
function NavigationHeader({
  icon,
  children,
}: {
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack direction="row" alignItems="center" gap={1}>
      <Box sx={MAIN_STYLES.iconContainer}>{icon}</Box>
      <Typography
        variant="caption"
        sx={{
          color: (theme) => theme.palette.grey50,
        }}
      >
        {children}
      </Typography>
    </Stack>
  );
}

/**
 * A back link component with an arrow icon, typically used in the header.
 * Can be used with either an href (renders as anchor) or onClick (renders as button).
 *
 * @example With href
 * ```tsx
 * <Navigation.BackLink href="/dashboard">
 *   <Navigation.Label>Back to Dashboard</Navigation.Label>
 * </Navigation.BackLink>
 * ```
 *
 * @example With onClick
 * ```tsx
 * <Navigation.BackLink onClick={() => navigate(-1)}>
 *   <Navigation.Label>Back to Dashboard</Navigation.Label>
 * </Navigation.BackLink>
 * ```
 */
function NavigationBackLink({
  href,
  onClick,
  children,
  sx,
}: {
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  sx?: SxProps<Theme>;
}) {
  return (
    <BoxLink
      href={href}
      onClick={onClick}
      sx={mixinSx(MAIN_STYLES.backLinkStyles, MAIN_STYLES.sharedItemStyles, sx)}
    >
      <Box sx={MAIN_STYLES.iconContainer}>
        <ArrowLeftIcon
          size={14}
          color={(theme) => theme.palette.text.secondary}
        />
      </Box>
      {children}
    </BoxLink>
  );
}

/**
 * A section component that provides the layout for navigation items.
 */
function NavigationItemContainer({
  children,
  onClick,
  href,
  isActive,
  'data-testid': testId,
}: {
  children: ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
  isActive?: boolean;
  'data-testid'?: string;
}) {
  return (
    <Box sx={MAIN_STYLES.itemLayout}>
      <BoxLink
        href={href}
        onClick={onClick}
        data-testid={testId}
        sx={mixinSx(
          MAIN_STYLES.sharedItemStyles,
          isActive && MAIN_STYLES.activeItemStyles
        )}
        aria-current={isActive ? 'page' : undefined}
        role="navigation"
      >
        {children}
      </BoxLink>
    </Box>
  );
}

/**
 * A label component for navigation items.
 */
function NavigationLabel({
  variant = 'h5',
  children,
}: PropsWithChildren<{ variant?: 'h5' | 'body2' }>) {
  return (
    <Typography variant={variant} component="span">
      {children}
    </Typography>
  );
}

/**
 * A navigation item that renders as a link with optional external indicator.
 * Supports view transitions for internal navigation.
 *
 * @example
 * ```tsx
 * <Navigation.Item href="/settings">
 *   <Navigation.Label>Settings</Navigation.Label>
 * </Navigation.Item>
 * ```
 *
 * ```tsx
 * <Navigation.Item href="https://docs.example.com" external>
 *   <Navigation.Label>Documentation</Navigation.Label>
 * </Navigation.Item>
 * ```
 */
function NavigationItem({
  href,
  external,
  onClick,
  children,
  'data-testid': testId,
}: {
  href: string;
  external?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  children: ReactNode;
  'data-testid'?: string;
}) {
  const router = useRouter();
  const startViewTransition = useViewTransition();
  const isActive = router.pathname === href;

  const handleClick = (e: React.MouseEvent) => {
    // If it's an external link or there's a custom onClick handler, skip the view transition
    if (external || onClick) {
      onClick?.(e);
      return;
    }

    // Prevent default navigation
    e.preventDefault();

    // If view transitions are supported, use them
    if (startViewTransition) {
      startViewTransition(() => {
        void router.push(href);
      });
    } else {
      // Fall back to regular navigation
      void router.push(href);
    }
  };

  return (
    <NavigationItemContainer
      href={href}
      onClick={handleClick}
      isActive={isActive}
      data-testid={testId}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        {children}
        {external ? (
          <PopOutIcon
            size={14}
            color={(theme) => theme.palette.text.secondary}
          />
        ) : null}
      </Stack>
    </NavigationItemContainer>
  );
}

/**
 * A group component that contains navigation items with a header.
 *
 * @example
 * ```tsx
 * <Navigation.Group icon={<SettingsIcon />} label="Settings">
 *   <Navigation.Item href="/settings/profile">
 *     <Navigation.Label>Profile</Navigation.Label>
 *   </Navigation.Item>
 * </Navigation.Group>
 * ```
 */
function NavigationGroup({
  label,
  icon,
  children,
}: {
  label: ReactNode;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <Stack>
      <NavigationHeader icon={icon}>{label}</NavigationHeader>
      <Stack gap={0.25} marginTop={0.5} marginBottom={1}>
        {children}
      </Stack>
    </Stack>
  );
}

/**
 * A collapsed header component that appears when the navigation is collapsed.
 * Typically used for mobile views to allow expanding the navigation.
 */
function NavigationCollapsedHeader({
  onClick,
  children,
  ref,
}: {
  onClick: () => void;
  children: ReactNode;
  ref?: RefObject<HTMLDivElement>;
}) {
  return (
    <Box ref={ref} sx={MAIN_STYLES.collapsedStyles}>
      <Stack direction="row" alignItems="center" gap={0.5}>
        <Button
          variant="text"
          isIcon
          size="large"
          onClick={onClick}
          sx={{
            '&:hover': {
              backgroundColor: (theme) => `${theme.palette.grey20}`,
            },
          }}
        >
          <SidebarRightIcon size={16} color={(theme) => theme.palette.grey50} />
        </Button>
        {children}
      </Stack>
    </Box>
  );
}

type NavigationBreadcrumbType = {
  ({ children, ...props }: { children: ReactNode }): JSX.Element;
  List: typeof NavigationBreadcrumbList;
  Item: typeof NavigationBreadcrumbItem;
  Text: typeof NavigationBreadcrumbText;
  Icon: typeof NavigationBreadcrumbIcon;
  Separator: typeof NavigationBreadcrumbSeparator;
};

/**
 * A breadcrumb system that provides hierarchical navigation.
 * Built as a compound component for maximum flexibility.
 *
 * @example Simple usage
 * ```tsx
 * <Navigation.Breadcrumb>
 *   <Navigation.Breadcrumb.List>
 *     <Navigation.Breadcrumb.Item>
 *       <Navigation.Breadcrumb.Text>Home</Navigation.Breadcrumb.Text>
 *     </Navigation.Breadcrumb.Item>
 *   </Navigation.Breadcrumb.List>
 * </Navigation.Breadcrumb>
 * ```
 *
 * @example With icons and separators
 * ```tsx
 * <Navigation.Breadcrumb>
 *   <Navigation.Breadcrumb.List>
 *     <Navigation.Breadcrumb.Item>
 *       <Navigation.Breadcrumb.Text>Home</Navigation.Breadcrumb.Text>
 *     </Navigation.Breadcrumb.Item>
 *     <Navigation.Breadcrumb.Separator />
 *     <Navigation.Breadcrumb.Item>
 *       <Navigation.Breadcrumb.Icon>
 *         <SettingsIcon />
 *       </Navigation.Breadcrumb.Icon>
 *       <Navigation.Breadcrumb.Text>Settings</Navigation.Breadcrumb.Text>
 *     </Navigation.Breadcrumb.Item>
 *   </Navigation.Breadcrumb.List>
 * </Navigation.Breadcrumb>
 * ```
 */
const NavigationBreadcrumb = Object.assign(
  function NavigationBreadcrumb({
    children,
    ...props
  }: {
    children: ReactNode;
  }) {
    return (
      <nav aria-label="Navigation breadcrumb" {...props}>
        {children}
      </nav>
    );
  },
  {
    List: NavigationBreadcrumbList,
    Item: NavigationBreadcrumbItem,
    Text: NavigationBreadcrumbText,
    Icon: NavigationBreadcrumbIcon,
    Separator: NavigationBreadcrumbSeparator,
  }
) as NavigationBreadcrumbType;

/**
 * Container for breadcrumb items. Handles layout and spacing.
 *
 * @example
 * ```tsx
 * <Navigation.Breadcrumb.List>
 *   <Navigation.Breadcrumb.Item>...</Navigation.Breadcrumb.Item>
 *   <Navigation.Breadcrumb.Separator />
 *   <Navigation.Breadcrumb.Item>...</Navigation.Breadcrumb.Item>
 * </Navigation.Breadcrumb.List>
 * ```
 */
function NavigationBreadcrumbList({ children }: { children: ReactNode }) {
  return (
    <Stack
      direction="row"
      gap={1}
      alignItems="center"
      component="ol"
      sx={{
        p: 0,
        m: 0,
        '& > li:last-child span': {
          color: (theme) => theme.palette.text.secondary,
        },
      }}
    >
      {children}
    </Stack>
  );
}

/**
 * Individual breadcrumb item container. Groups related elements.
 *
 * @example
 * ```tsx
 * <Navigation.Breadcrumb.Item>
 *   <Navigation.Breadcrumb.Icon>{icon}</Navigation.Breadcrumb.Icon>
 *   <Navigation.Breadcrumb.Text>Label</Navigation.Breadcrumb.Text>
 * </Navigation.Breadcrumb.Item>
 * ```
 */
function NavigationBreadcrumbItem({ children }: { children: ReactNode }) {
  return (
    <Stack direction="row" gap={1} alignItems="center" component="li">
      {children}
    </Stack>
  );
}

/**
 * Text element in a breadcrumb item. Handles typography styles.
 * The last breadcrumb text in the list will automatically use text.secondary color.
 *
 * @example
 * ```tsx
 * <Navigation.Breadcrumb.Text>Settings</Navigation.Breadcrumb.Text>
 * ```
 */
function NavigationBreadcrumbText({ children }: { children: ReactNode }) {
  return (
    <Typography
      component="span"
      variant="h4"
      sx={{
        color: (theme) => theme.palette.grey50,
      }}
      noWrap
    >
      {children}
    </Typography>
  );
}

/**
 * Icon container in a breadcrumb item. Handles alignment and accessibility.
 *
 * @example
 * ```tsx
 * <Navigation.Breadcrumb.Icon>
 *   <SettingsIcon size={16} />
 * </Navigation.Breadcrumb.Icon>
 * ```
 */
function NavigationBreadcrumbIcon({ children }: { children: ReactNode }) {
  return (
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'center' }}
      aria-hidden="true"
    >
      {children}
    </Box>
  );
}

/**
 * Separator between breadcrumb items. Renders as a forward slash by default.
 *
 * @example
 * ```tsx
 * <Navigation.Breadcrumb.Item>Home</Navigation.Breadcrumb.Item>
 * <Navigation.Breadcrumb.Separator />
 * <Navigation.Breadcrumb.Item>Settings</Navigation.Breadcrumb.Item>
 * ```
 */
function NavigationBreadcrumbSeparator() {
  return (
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        fontSize: '12px',
        color: (theme) => theme.palette.grey50,
      }}
      aria-hidden="true"
    >
      /
    </Box>
  );
}

/**
 * In-page navigation container component.
 */
const NavigationInPage = forwardRef<
  HTMLDivElement,
  PropsWithChildren<{ sx?: SxProps<Theme> }>
>(({ sx, children }, ref) => {
  return (
    <Stack
      ref={ref}
      component="aside"
      gap={1}
      sx={mixinSx(IN_PAGE_STYLES.inPageContainer, sx)}
    >
      {children}
    </Stack>
  );
});
NavigationInPage.displayName = 'NavigationInPage';

/**
 * In-page navigation section component.
 */
function NavigationInPageSection({
  category,
  sx,
  children,
}: PropsWithChildren<{
  category?: string;
  sx?: SxProps<Theme>;
}>) {
  return (
    <Stack
      gap={1}
      sx={mixinSx(IN_PAGE_STYLES.inPageSection, sx)}
      component="nav"
    >
      {category && (
        <Typography
          sx={{ color: (theme) => theme.palette.grey50 }}
          variant="caption"
        >
          {category}
        </Typography>
      )}
      <Stack gap={0.25}>{children}</Stack>
    </Stack>
  );
}

/**
 * In-page navigation item component.
 */
function NavigationInPageItem({
  sectionId,
  sectionTitle,
  isActive,
  isSubItem,
  onClick,
  'data-testid': testId,
}: {
  sectionId: string;
  sectionTitle: string;
  isActive?: boolean;
  isSubItem?: boolean;
  onClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  'data-testid'?: string;
}) {
  return (
    <Box
      component="a"
      href={`#${sectionId}`}
      data-toc-item={sectionId}
      onClick={onClick}
      aria-current={isActive}
      sx={mixinSx(IN_PAGE_STYLES.inPageItem, isSubItem && { ml: 2 })}
      data-testid={testId}
    >
      <Typography variant={isSubItem ? 'body2' : 'h5'} component="span">
        {sectionTitle}
      </Typography>
    </Box>
  );
}

type NavigationTreeRootProps = {
  children: ReactNode;
  'aria-label': string;
  sx?: SxProps<Theme>;
};

/**
 * Root container for tree navigation. Provides the proper ARIA tree role.
 * Must be used as the parent of NavigationTreeItem and NavigationTreeCollapsible components.
 *
 * @example
 * ```tsx
 * <Navigation.TreeRoot aria-label="Documentation navigation">
 *   <Navigation.TreeItem href="/intro" label="Introduction" ... />
 *   <Navigation.TreeCollapsible href="/guide" label="Guide" ...>
 *     <Navigation.TreeItem href="/guide/start" label="Getting started" ... />
 *   </Navigation.TreeCollapsible>
 * </Navigation.TreeRoot>
 * ```
 */
function NavigationTreeRoot({
  children,
  'aria-label': ariaLabel,
  sx,
}: NavigationTreeRootProps) {
  return (
    <Stack
      component="ul"
      role="tree"
      aria-label={ariaLabel}
      gap={0.25}
      sx={mixinSx({ p: 0, m: 0, listStyle: 'none' }, sx)}
    >
      {children}
    </Stack>
  );
}

type NavigationTreeItemProps = {
  href: string;
  label: ReactNode;
  depth: number;
  isActive: boolean;
  isAncestorActive: boolean;
  onNavigate: (href: string) => void;
  variant: 'category' | 'page';
  startIcon?: ReactNode;
  endAdornment?: ReactNode;
  /**
   * The HTML element to render as. Defaults to 'li' with full tree item semantics.
   * Use 'div' when the item is already wrapped by a parent list item (e.g., in NavigationTreeCollapsible).
   */
  component?: 'li' | 'div';
  /**
   * ID to apply to the link element, used for aria-labelledby on parent treeitems.
   */
  labelId?: string;
};

function NavigationTreeItem({
  href,
  label,
  depth,
  isActive,
  isAncestorActive,
  onNavigate,
  variant,
  startIcon,
  endAdornment,
  component = 'li',
  labelId,
}: NavigationTreeItemProps) {
  const isTreeItem = component === 'li';
  return (
    <Box
      component={component}
      role={isTreeItem ? 'treeitem' : undefined}
      aria-selected={isTreeItem ? isActive : undefined}
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        paddingInline: 1,
        paddingBlock: 0.5,
        marginInlineStart: depth === 0 ? 0 : theme.spacing(depth * 1.5),
        borderRadius: 1,
        transition: theme.transitions.create(['background-color', 'color'], {
          duration: theme.transitions.duration.short,
        }),
        backgroundColor: isActive
          ? theme.palette.grey20
          : isAncestorActive
            ? theme.palette.grey10
            : 'transparent',
        '&:hover': {
          backgroundColor: theme.palette.grey20,
        },
      })}
    >
      <BoxLink
        id={labelId}
        href={href}
        onClick={() => onNavigate(href)}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          flexGrow: 1,
          display: 'flex',
          alignItems: 'center',
          gap: startIcon ? 1 : 0,
          textDecoration: 'none',
          color: 'inherit',
          borderRadius: 1,
          '&:focus-visible': {
            outline: (theme) => `1.5px solid ${theme.palette.primary.main}`,
            outlineOffset: '1.5px',
          },
        }}
      >
        {startIcon ? (
          <Box
            component="span"
            aria-hidden="true"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            {startIcon}
          </Box>
        ) : null}
        <NavigationLabel variant={variant === 'category' ? 'h5' : 'body2'}>
          {label}
        </NavigationLabel>
      </BoxLink>
      {endAdornment}
    </Box>
  );
}

type NavigationTreeCollapsibleProps = {
  href: string;
  label: ReactNode;
  depth: number;
  isActive: boolean;
  isAncestorActive: boolean;
  onNavigate: (href: string) => void;
  isExpanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
  toggleAriaLabel: string;
  children: ReactNode;
};

function NavigationTreeCollapsible({
  href,
  label,
  depth,
  isActive,
  isAncestorActive,
  onNavigate,
  isExpanded,
  onExpandedChange,
  toggleAriaLabel,
  children,
}: NavigationTreeCollapsibleProps) {
  const labelId = useId();
  const contentId = useId();

  return (
    <Box
      component="li"
      role="treeitem"
      aria-expanded={isExpanded}
      aria-selected={isActive}
      aria-labelledby={labelId}
      sx={{ display: 'contents' }}
    >
      <NavigationTreeItem
        href={href}
        label={label}
        depth={depth}
        isActive={isActive}
        isAncestorActive={isAncestorActive}
        onNavigate={onNavigate}
        variant="category"
        component="div"
        labelId={labelId}
        endAdornment={
          <IconButton
            aria-label={toggleAriaLabel}
            aria-controls={contentId}
            aria-expanded={isExpanded}
            onClick={() => onExpandedChange(!isExpanded)}
            size="small"
            edge="end"
            sx={{
              borderRadius: '4px',
              padding: 0.5,
              color: (theme) => theme.palette.text.secondary,
              '&:active': {
                // MUI uses a CSS variable for hover internally & we want active to match that state
                backgroundColor: 'var(--IconButton-hoverBg, unset)',
              },
            }}
          >
            <CollapseIcon size={16} />
          </IconButton>
        }
      />
      <Collapse
        in={isExpanded}
        sx={{
          p: 0,
          m: 0,
          listStyle: 'none',
          [`& .${collapseClasses.wrapperInner}`]: {
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
            paddingBlockStart: 0.25,
          },
        }}
        component="ul"
        role="group"
        id={contentId}
      >
        {children}
      </Collapse>
    </Box>
  );
}

type NavigationType = {
  Root: typeof Navigation;
  Header: typeof NavigationHeader;
  BackLink: typeof NavigationBackLink;
  Group: typeof NavigationGroup;
  Item: typeof NavigationItem;
  Label: typeof NavigationLabel;
  CollapsedHeader: typeof NavigationCollapsedHeader;
  Breadcrumb: typeof NavigationBreadcrumb & {
    List: typeof NavigationBreadcrumbList;
    Item: typeof NavigationBreadcrumbItem;
    Text: typeof NavigationBreadcrumbText;
    Icon: typeof NavigationBreadcrumbIcon;
    Separator: typeof NavigationBreadcrumbSeparator;
  };
  InPage: typeof NavigationInPage;
  InPageSection: typeof NavigationInPageSection;
  InPageItem: typeof NavigationInPageItem;
  TreeRoot: typeof NavigationTreeRoot;
  TreeItem: typeof NavigationTreeItem;
  TreeCollapsible: typeof NavigationTreeCollapsible;
};

// Attach the subcomponents to Navigation
Navigation.Root = Navigation;
Navigation.Header = NavigationHeader;
Navigation.BackLink = NavigationBackLink;
Navigation.Group = NavigationGroup;
Navigation.Item = NavigationItem;
Navigation.Label = NavigationLabel;
Navigation.CollapsedHeader = NavigationCollapsedHeader;
Navigation.Breadcrumb = NavigationBreadcrumb;
Navigation.Breadcrumb.List = NavigationBreadcrumbList;
Navigation.Breadcrumb.Item = NavigationBreadcrumbItem;
Navigation.Breadcrumb.Text = NavigationBreadcrumbText;
Navigation.Breadcrumb.Icon = NavigationBreadcrumbIcon;
Navigation.Breadcrumb.Separator = NavigationBreadcrumbSeparator;
Navigation.InPage = NavigationInPage;
Navigation.InPageSection = NavigationInPageSection;
Navigation.InPageItem = NavigationInPageItem;
Navigation.TreeRoot = NavigationTreeRoot;
Navigation.TreeItem = NavigationTreeItem;
Navigation.TreeCollapsible = NavigationTreeCollapsible;

export default Navigation as NavigationType;
